import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Loader2, Undo2 } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { personaRefusalKind, personaWeightsAtRisk } from '@/utils/personaRefusal';

/**
 * ⛔⛔ THE DOOR BEHIND THE SENTENCE.
 *
 * `preserveBenefitWeights` refuses a refresh that would strand curated benefit-weights, and its own
 * message says "THERE IS NO CONTROL TODAY THAT RECONCILES PERSONA KEYS". This panel is that control.
 *
 * 🔑 IT SHOWS THE WEIGHTS. The operator is deciding where real, hand-tuned numbers go — A PANEL THAT
 * HIDES THE WEIGHTS TURNS A DECISION INTO A GUESS. Every stranded persona is rendered with its full
 * vector, not a count.
 *
 * ⚠️ NO DEFAULT THAT LOSES DATA. An undecided stranded persona blocks Apply; there is no
 * "skip"/"leave" that quietly discards. Silence is not consent — that is the entire reason the guard
 * exists, and a panel that defaulted to drop would be the original defect wearing the fix's clothes.
 */

export interface StrandedPersona {
  index: number;
  persona_key: string | null;
  label: string | null;
  benefit_weights: Record<string, number>;
  /**
   * ⛔⛔ CANONICAL BENEFIT KEYS THIS CURATED VECTOR DOES NOT HAVE.
   * A vector curated months ago is INCOMPLETE once new canonical benefits are created — Bicycle Tires
   * carries 8 where its category now has 13. Apply fills only these, from the researched persona the
   * operator picks, and every other key stays the human's. Destination-independent by construction:
   * the SET is (canonical − curated); only the VALUES depend on the choice. (task-7fk5xr1v6f.)
   */
  missing_benefit_ids?: string[];
}
/**
 * ⛔⛔ A CONFLICTING PERSONA IS DECIDABLE TOO, AND IT USED NOT TO BE.
 *
 * The backend reported conflicts as bare KEY STRINGS while decisions are keyed by STORED ARRAY INDEX.
 * So a conflict-only preview rendered a heading, no controls, and an ENABLED Apply — which submitted
 * `{}` and came back with the identical refusal. A dead end with a button on it, which is the exact
 * thing this whole panel exists to remove. (Both codex legs, BLOCKING @967fdb227.)
 * ⇒ `conflictingDetail` carries the same addressable shape as a stranded persona, plus the key the
 * two personas are fighting over, so both sides of the conflict get a control.
 */
export interface ConflictingPersona extends StrandedPersona {
  conflict_key: string;
}
export interface ResearchedPersona {
  persona_key: string;
  label?: string;
  description?: string;
  /** The full canonical-length vector — the SOURCE of any topped-up value, so the panel can show it. */
  benefit_weights?: Record<string, number>;
}
type Decision = { action: 'map'; to: string } | { action: 'drop' };

/** A durable applied session this category can still reverse, as the sector endpoint reports it. */
export interface UndoableSession {
  sessionId: string;
  categoryPath: string;
  appliedAt?: string | null;
  appliedBy?: string | null;
  undoable: boolean;
}

interface Props {
  sectorId: number;
  categoryPath: string;
  /**
   * The research_error, persisted OR from this session. ⚠️ No longer the only thing that decides
   * whether the panel is offered — see `personas` below and `personaWeightsAtRisk`.
   */
  researchError: string | null | undefined;
  /**
   * The category's STORED personas. ⛔ THIS IS WHAT MAKES THE CONTROL REACHABLE. A preservation
   * refusal restores the row and clears research_error by design, so gating on the error alone hid
   * this panel on every category that had just refused. Weights in the stored data are the durable
   * precondition; the error is only a hint about which copy to show.
   */
  personas?: unknown;
  onApplied?: () => void;
}

export function PersonaReconciliationPanel({ sectorId, categoryPath, researchError, personas, onApplied }: Props) {
  const kind = personaRefusalKind(researchError);
  const atRisk = personaWeightsAtRisk(personas);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<null | 'preview' | 'apply' | 'undo'>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [stranded, setStranded] = useState<StrandedPersona[]>([]);
  const [researched, setResearched] = useState<ResearchedPersona[]>([]);
  const [conflicting, setConflicting] = useState<string[]>([]);
  const [conflictingDetail, setConflictingDetail] = useState<ConflictingPersona[]>([]);
  const [decisions, setDecisions] = useState<Record<number, Decision>>({});
  const [appliedSessionId, setAppliedSessionId] = useState<string | null>(null);
  const [confirmingDrops, setConfirmingDrops] = useState(false);
  const [undoneIds, setUndoneIds] = useState<string[]>([]);
  const [undoableSession, setUndoableSession] = useState<UndoableSession | null>(null);
  const [benefitLabels, setBenefitLabels] = useState<Record<string, string>>({});

  /**
   * ⛔⛔ EVERY PIECE OF STATE HERE BELONGS TO ONE CATEGORY, AND THE COMPONENT IS REUSED ACROSS THEM.
   *
   * CategoryDetailDialog keeps ONE instance and swaps `category`, so opening a different category
   * changed the props without unmounting. The probe for the new category is asynchronous, so until it
   * returned — and FOREVER if it failed — `undoableSession` and `appliedSessionId` still held the
   * PREVIOUS category's session, and the Undo button would have reverted A DIFFERENT CATEGORY'S
   * reconciliation. A destructive action pointed at the wrong row, with nothing on screen to say so.
   * 🔑 THE WRONG-TREE CLASS, IN A COMPONENT: state outlived the identity it described. The effect's
   * `cancelled` flag stops a stale RESPONSE from landing; it does nothing about stale STATE that is
   * already there. (codex leg 2, BLOCKING @3d31eb4fe.)
   *
   * ⚠️ SYNCHRONOUS, DURING RENDER — not in an effect. An effect runs AFTER the render that already
   * showed the button, so there would still be a frame in which Undo is visible and points at the old
   * session. This is React's documented "adjust state when props change" pattern precisely because a
   * useEffect reset is a beat too late.
   */
  const identity = `${sectorId}\u0000${categoryPath}`;
  const [lastIdentity, setLastIdentity] = useState(identity);
  if (lastIdentity !== identity) {
    setLastIdentity(identity);
    setUndoableSession(null);
    setBenefitLabels({});
    setAppliedSessionId(null);
    setUndoneIds([]);
    setSessionId(null);
    setStranded([]); setResearched([]); setConflicting([]); setConflictingDetail([]);
    setDecisions({});
    setOpen(false); setConfirmingDrops(false); setError(null); setBusy(null);
  }

  /**
   * ⛔⛔ RECOVER THE DURABLE UNDO ON MOUNT. `appliedSessionId` lives in React state, so a reload or a
   * navigation away lost the only handle on a reversible write while the before-image sat in the
   * table, reachable by nothing. DURABLE IS NECESSARY; REACHABLE IS THE OTHER HALF.
   * (codex leg 1, BLOCKING @c5203a456 — the persistence twin of "Undo vanishes on success".)
   *
   * ⚠️ ONE REQUEST PER OPENED CATEGORY, not per row: this panel lives inside CategoryDetailDialog,
   * which renders for the SINGLE selected category (CategoryResearch.tsx:4547). I checked that call
   * site rather than assuming, having first built a sector-scoped endpoint to avoid a per-row cost
   * that does not exist.
   *
   * ⛔ A FAILURE HERE IS SILENT ON PURPOSE, AND ONLY THIS ONE. It is a background recovery probe, not
   * an action the operator took; surfacing "could not check for an undoable session" above a refusal
   * they are trying to resolve would bury the message that matters. It is logged, never swallowed —
   * and it cannot hide a failed UNDO, which is a pressed button and reports loudly.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(
          `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/reconcile-personas/undoable`
        );
        const data = await res.json();
        if (!cancelled && res.ok && data.success) setUndoableSession(data.session || null);
        else if (!res.ok) console.warn('[PersonaReconciliation] undoable probe failed:', data?.error || res.status);
      } catch (e) {
        console.warn('[PersonaReconciliation] undoable probe failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [sectorId, categoryPath]);

  /**
   * ⇒ The session this panel can undo: whichever it applied ITSELF this mount, or — after a reload,
   * when React state is gone — the one the server still reports as reversible.
   * ⚠️ A session reported with `undoable: false` is deliberately NOT offered: its personas have moved
   * since the apply, so the writer would refuse. OFFERING A BUTTON THE WRITER WOULD REJECT IS THE
   * DEAD-END SHAPE THIS WHOLE CONTROL EXISTS TO REMOVE — the staleness answer comes from one shared
   * predicate on the server so the list and the writer cannot disagree.
   */
  const candidate = appliedSessionId
    || (undoableSession && undoableSession.undoable ? undoableSession.sessionId : null);
  // ⛔ A session THIS mount already undid must not be re-offered. The prop comes from a sector read the
  // page took earlier and does not re-run on every action, so without this the Undo button would
  // survive its own success and the second press would be refused ("only an APPLIED reconciliation can
  // be undone") — a control that appears broken at the moment it just worked.
  const undoTarget = candidate && !undoneIds.includes(candidate) ? candidate : null;

  // ⇒ Not a refusal this panel can resolve: render nothing rather than a button that cannot help.
  // A CONTROL OFFERED WHERE IT CANNOT ACT TEACHES THE OPERATOR THAT THE CONTROL IS BROKEN.
  //
  // ⛔⛔ ...BUT NOT ONCE AN APPLY HAS HAPPENED IN THIS SESSION, AND THAT OMISSION DELETED UNDO.
  // Apply CLEARS research_error, then calls onApplied() to reload the category — so `researchError`
  // comes back null, `kind` becomes null, and this line unmounted the whole panel INCLUDING the Undo
  // button, at the exact moment undo becomes meaningful. Success made the escape hatch vanish.
  // 🔑 THE CONDITION THAT DECIDES WHETHER TO OFFER A CONTROL WAS REUSED TO DECIDE WHETHER TO KEEP IT.
  // Those are different questions: the refusal justifies OPENING the panel, the applied session
  // justifies KEEPING it. A REVERSIBLE ACTION WHOSE UNDO DISAPPEARS ON SUCCESS IS NOT REVERSIBLE —
  // and every backend test for undo passed, because none of them could see the button.
  // (codex leg 1, BLOCKING @6991af902.)
  // ⛔ `atRisk` IS THE THIRD TERM AND IT IS THE ONE THAT MAKES THIS REACHABLE AT ALL. Gating on
  // `kind` alone required a PERSISTED refusal, and the refusal path deliberately persists nothing
  // (CategoryCharacteristicsService:2436 restores the row and NULLs research_error). Measured
  // 2026-09-08: 0 of 56 candidate categories carried one, so the panel mounted on none of them.
  if (!kind && !undoTarget && !atRisk) return null;

  const runPreview = async () => {
    setBusy('preview'); setError(null);
    try {
      const res = await authFetch(
        `/category-characteristics/${sectorId}/category/${encodeURIComponent(categoryPath)}/reconcile-personas/preview`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Preview failed (HTTP ${res.status})`);
      // ⛔⛔ THE `|| []` ON THE NEXT LINES IS EXACTLY HOW THIS DEFECT WOULD COME BACK.
      // A backend that stopped sending `conflictingDetail` while still sending `conflicting` would
      // land here as an empty list, render no controls, enable Apply on `{}` — the precise blocking
      // defect just fixed, silently restored by a defensive default.
      // 🔑 A REMEDY IS THE LIKELIEST PLACE FOR THE NEXT INSTANCE OF ITS OWN DEFECT, and a default that
      // absorbs a missing field is how it re-enters. So the INCONSISTENCY IS NAMED, not absorbed.
      if ((data.conflicting || []).length > 0 && (data.conflictingDetail || []).length === 0) {
        throw new Error(
          'The server reported conflicting persona keys but sent nothing addressable to decide about, '
          + 'so this panel cannot offer a choice it could honour. Nothing has been changed. This is a '
          + 'version mismatch between the admin and the backend, not a problem with your category.'
        );
      }
      setSessionId(data.sessionId);
      setStranded(data.stranded || []);
      setResearched(data.researched || []);
      setConflicting(data.conflicting || []);
      setConflictingDetail(data.conflictingDetail || []);
      setBenefitLabels(data.benefitLabels || {});
      setDecisions({});
      setOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * ⛔⛔ EVERY PERSONA THE BACKEND WILL REFUSE OVER, NOT JUST THE STRANDED ONES.
   *
   * This was `stranded` alone. A conflict-only session therefore had ZERO rows here, `undecided` was
   * empty, and Apply came up ENABLED on an empty decision map — straight back to the same refusal.
   * ⇒ The two lists are disjoint by construction: a persona is stranded when its key has NO successor
   * in the research, and conflicting when its key HAS one and two stored personas claim it. So
   * concatenating cannot double-render an index, and each index still owns exactly one decision.
   */
  const decidable: Array<StrandedPersona & { conflict_key?: string }> = [...stranded, ...conflictingDetail];
  const undecided = decidable.filter((s) => !decisions[s.index]);

  /**
   * ⛔⛔ THE ONE IRREVERSIBLE-FEELING ACTION IN THE WHOLE CONTROL, AND IT HAD NO CONFIRMATION.
   *
   * Selecting "Discard these weights" and pressing Apply deleted curated vectors immediately. A
   * dropdown selection is easy to make by ACCIDENT — it sits one row above Apply, and the option list
   * is navigated by keyboard — so a misclick destroyed hand-tuned data that took a person an afternoon.
   * The PRD requires this explicitly (§7: "Discard these weights (with a plain confirm, not a
   * type-to-confirm — the guard already made the safe choice; this is the operator overriding it
   * deliberately)") — AND I WROTE THAT PRD, then shipped the panel without it.
   * 🔑 A DOCUMENT REQUIRING A SAFEGUARD IS NOT THE SAFEGUARD, EVEN WHEN THE SAME PERSON WROTE BOTH.
   * (codex leg 1, BLOCKING @504fe2977 — the same review where leg 2 dropped to minor, which is how I
   * missed it: I read the top of the list and took the improvement as the whole story.)
   *
   * ⚠️ A PLAIN CONFIRM, exactly as specified — no type-to-confirm. The guard has ALREADY made the safe
   * choice by refusing; this step exists so the override is deliberate, not so it is arduous. And it
   * lists the actual vectors, because a confirmation that does not say what is being destroyed is a
   * speed bump rather than a decision.
   */
  const drops = decidable.filter((s) => decisions[s.index]?.action === 'drop');

  const performApply = async () => {
    if (!sessionId) return;
    setConfirmingDrops(false);
    setBusy('apply'); setError(null);
    try {
      const review = await authFetch(`/category-characteristics/reconcile-personas/${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisions }),
      });
      const reviewData = await review.json();
      if (!review.ok || !reviewData.success) throw new Error(reviewData.error || 'Recording decisions failed');

      const res = await authFetch(`/category-characteristics/reconcile-personas/${sessionId}/apply`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Apply failed (HTTP ${res.status})`);
      setAppliedSessionId(sessionId);
      setOpen(false);
      onApplied?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * ⇒ THE GATE. With no drop decisions this is exactly the old behaviour — a confirmation nobody needs
   * is a dialog people learn to dismiss without reading, which makes the one that matters weaker.
   */
  const apply = async () => {
    if (drops.length > 0) { setConfirmingDrops(true); return; }
    await performApply();
  };

  const undo = async () => {
    if (!undoTarget) return;
    setBusy('undo'); setError(null);
    try {
      const res = await authFetch(`/category-characteristics/reconcile-personas/${undoTarget}/undo`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Undo failed (HTTP ${res.status})`);
      setUndoneIds((prev) => [...prev, undoTarget]);
      setAppliedSessionId(null);
      onApplied?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 space-y-2" data-testid="persona-reconciliation">
      {!open && !undoTarget && (
        <Button size="sm" variant="outline" onClick={runPreview} disabled={busy !== null}>
          {busy === 'preview' ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
          Reconcile personas
        </Button>
      )}

      {/* ⇒ Undo is offered ONLY after an apply in this session, because it is the only moment we hold
          the session id. Reversibility is mandatory: A PERSONA EDIT WITH NO UNDO IS NOT A DECISION
          TOOL, IT IS A TRAPDOOR. */}
      {undoTarget && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary">Reconciliation applied</Badge>
          <Button size="sm" variant="outline" onClick={undo} disabled={busy !== null}>
            <Undo2 className="h-3 w-3 mr-2" /> Undo
          </Button>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription data-testid="reconcile-error">{error}</AlertDescription>
        </Alert>
      )}

      {open && (
        <div className="border rounded-md p-3 space-y-3">
          <div className="text-sm">
            {/* ⚠️ The two refusals get DIFFERENT copy. "Choose where these weights go" is meaningless
                advice for a duplicate key, and telling an operator the wrong thing confidently is
                worse than telling them nothing. */}
            {kind === 'stranded'
              ? <>This refresh renamed or dropped personas that carry hand-tuned weights. Choose where each one&apos;s weights should go, or discard them explicitly.</>
              : kind === 'conflicting'
              ? <>The <strong>stored</strong> personas were already ambiguous before this refresh: a persona key appears more than once with different weights. Discard the vector that should not win.</>
              /* ⚠️ THE NO-REFUSAL CASE, and it must not claim one happened. Offered because the
                 stored personas carry weights, so a plain refresh WOULD refuse rather than strand
                 them. Preview writes nothing, so this is safe to open speculatively. */
              : <>These personas carry benefit weights, so a plain refresh will <strong>refuse</strong> rather than lose them. Preview the research to see which keys survive and decide where each set of weights should go. <strong>Nothing is written until you apply.</strong></>}
          </div>

          {conflicting.length > 0 && (
            <Alert>
              <AlertDescription className="text-xs">
                Duplicate stored keys: {conflicting.join(', ')}
              </AlertDescription>
            </Alert>
          )}

          {/* ⛔⛔ SAY IT WHEN THE ONLY AVAILABLE DECISION IS DESTRUCTIVE.
              With no researched destinations the dropdown offers "Discard" and nothing else — which is
              indistinguishable, on screen, from the bug where the destination list was never sent at
              all (it always rendered empty, so Discard was the only reachable outcome). An operator
              who meets that silently concludes the control only deletes.
              🔑 THE FIXED VERSION AND THE BROKEN ONE LOOKED IDENTICAL, SO THE STATE MUST NAME ITSELF. */}
          {decidable.length > 0 && researched.length === 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs" data-testid="no-destinations">
                This research produced no personas with keys, so there is nowhere to move these weights
                to — discarding is the only thing this panel could do, and that would lose them. Cancel
                and re-run the research rather than discarding.
              </AlertDescription>
            </Alert>
          )}

          {decidable.map((s) => (
            <div key={s.index} className="border rounded p-2 text-sm" data-testid={`stranded-${s.index}`}>
              <div className="font-medium">
                {s.label || s.persona_key || `(persona with no key, position ${s.index + 1})`}
                {s.conflict_key && (
                  <Badge variant="outline" className="ml-2 text-xs" data-testid={`conflict-badge-${s.index}`}>
                    duplicate key: {s.conflict_key}
                  </Badge>
                )}
              </div>
              {/* 🔑 THE WEIGHTS, IN FULL. This is the thing being moved. */}
              <div className="text-xs text-muted-foreground mt-1">
                {Object.entries(s.benefit_weights || {}).map(([k, v]) => `${k}: ${v}`).join('  ·  ') || '(no weights)'}
              </div>
              {/* ⛔⛔ THE TOP-UP, SHOWN BEFORE IT HAPPENS AND WITH ITS NUMBERS.
                  The operator is accepting values they did not tune, so "5 keys will be filled" is not
                  enough — A PANEL THAT HIDES THE NUMBERS TURNS ACCEPTANCE INTO A GUESS, the same rule
                  that put the full vectors on the stranded personas. Rendered only once a destination
                  is chosen, because the VALUES come from that choice.
                  🔑 NOTHING HERE IS INVENTED: every filled value is read from the researched persona
                  the operator selected, and apply records its provenance. */}
              {(s.missing_benefit_ids?.length ?? 0) > 0 && (
                <div className="mt-2 text-xs rounded border border-amber-300/60 bg-amber-50/50 p-2"
                     data-testid={`topup-${s.index}`}>
                  <div>
                    <strong>{Object.keys(s.benefit_weights || {}).length} curated weights are kept exactly.</strong>{' '}
                    This category has gained {s.missing_benefit_ids!.length} benefit(s) since they were
                    tuned; those will be filled from the persona you choose — not invented.
                  </div>
                  {(() => {
                    const d = decisions[s.index];
                    if (!d || d.action !== 'map') {
                      return <div className="mt-1 text-muted-foreground">Choose a destination to see the values.</div>;
                    }
                    const src = researched.find((r) => r.persona_key === d.to);
                    const w = src?.benefit_weights || {};
                    return (
                      <div className="mt-1" data-testid={`topup-values-${s.index}`}>
                        Filled from <strong>{src?.label || d.to}</strong>:{' '}
                        {/* ⛔ NAME, or the FULL id — never a truncated one. Canonical benefit UUIDs in
                            one category share prefixes: the first version showed id.slice(0,8) and my
                            own fixture gave all five missing ids the SAME eight characters. FIVE
                            IDENTICAL LABELS BESIDE FIVE DIFFERENT NUMBERS LOOKS PRECISE AND IDENTIFIES
                            NOTHING. (codex leg 1, minor.) */}
                        {s.missing_benefit_ids!.map((id) => `${benefitLabels[id] || id}: ${w[id] ?? '—'}`).join('  ·  ')}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="mt-2 flex items-center gap-2">
                <select
                  className="border rounded px-2 py-1 text-sm"
                  data-testid={`decision-${s.index}`}
                  value={decisions[s.index]
                    ? (decisions[s.index].action === 'drop' ? '__drop__' : (decisions[s.index] as { to: string }).to)
                    : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDecisions((prev) => {
                      const next = { ...prev };
                      if (v === '') delete next[s.index];
                      else if (v === '__drop__') next[s.index] = { action: 'drop' };
                      else next[s.index] = { action: 'map', to: v };
                      return next;
                    });
                  }}
                >
                  {/* ⛔ The empty option is "not yet decided", NOT a benign default — Apply stays
                      disabled while any remain. There is deliberately no "skip". */}
                  <option value="">— choose —</option>
                  {researched.map((r) => (
                    <option key={r.persona_key} value={r.persona_key}>
                      Move weights to: {r.label || r.persona_key}
                    </option>
                  ))}
                  <option value="__drop__">Discard these weights</option>
                </select>
              </div>
            </div>
          ))}

          {/* ⛔⛔ THE PLAIN CONFIRM THE PRD REQUIRES, SHOWN ONLY WHEN SOMETHING WILL ACTUALLY BE
              DESTROYED. It NAMES the personas and PRINTS the vectors: a confirmation that does not say
              what is being destroyed is a speed bump, not a decision — and this panel's whole premise
              is that hiding the weights turns a decision into a guess. */}
          {confirmingDrops && (
            <Alert variant="destructive" data-testid="drop-confirm">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs space-y-2">
                <div>
                  <strong>
                    {drops.length === 1
                      ? 'This will permanently discard the hand-tuned weights of 1 persona.'
                      : `This will permanently discard the hand-tuned weights of ${drops.length} personas.`}
                  </strong>{' '}
                  The guard refused this refresh to protect them; discarding is you overriding that
                  deliberately. Undo restores them only until the category is researched again.
                </div>
                {drops.map((d) => (
                  <div key={d.index} data-testid={`drop-summary-${d.index}`}>
                    {d.label || d.persona_key || `(persona at position ${d.index + 1})`} —{' '}
                    {Object.entries(d.benefit_weights || {}).map(([k, v]) => `${k}: ${v}`).join('  ·  ') || '(no weights)'}
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <Button size="sm" variant="destructive" onClick={performApply} data-testid="drop-confirm-yes">
                    Discard and apply
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmingDrops(false)} data-testid="drop-confirm-cancel">
                    Cancel
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-3">
            <Button size="sm" onClick={apply} disabled={busy !== null || undecided.length > 0 || confirmingDrops} data-testid="reconcile-apply">
              {busy === 'apply' ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
              Apply reconciliation
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy !== null}>Cancel</Button>
            {undecided.length > 0 && (
              <span className="text-xs text-muted-foreground" data-testid="undecided-note">
                {undecided.length} persona(s) still need a decision — nothing is written until every one is decided.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonaReconciliationPanel;
